// TanStack Query hooks for the RFC-0006 projects read endpoints (RFC-0009
// Task 1.2; ADR-0007). Each hook wraps `apiGet` (lib/api-client.ts) and inherits
// the shared `retry` predicate from the queryClient (no 401/4xx retries).
//
// Query keys are structured so Phase 2's connect/sync mutations can invalidate a
// whole family at once — `invalidateQueries({ queryKey: ['projects'] })` after a
// successful connect (RFC-0002) refreshes every list + summary + detail query.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  ProjectListItem,
  ProjectSummary,
  ProjectDetailResponse,
  RepositoryStatus,
} from '@portal/shared-types';
import { apiGet, type QueryParams } from '@/lib/api-client';

/**
 * The G14 pagination wrapper (`apps/api/src/lib/pagination.ts` `PaginatedResponse`).
 * Declared locally because that type lives in `apps/api` and the web app cannot
 * import across the API boundary; the shape is fixed by RFC-0006.
 */
export interface Paginated<T> {
  data: T[];
  pagination: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
  };
}

/** Filters + pagination accepted by `useProjects` (mapped to RFC-0006 params). */
export interface ProjectListParams {
  q?: string;
  status?: RepositoryStatus;
  types?: string[];
  owners?: string[];
  tags?: string[];
  manifestBroken?: boolean;
  syncFailed?: boolean;
  stale?: boolean;
  page?: number;
  perPage?: number;
}

export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (params: ProjectListParams) => [...projectKeys.lists(), params] as const,
  summary: () => [...projectKeys.all, 'summary'] as const,
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (repo: string) => [...projectKeys.details(), repo] as const,
  // RFC-0010 Task 3.1 — the doc-detail family under a repo's detail key, so a
  // connect/sync invalidation of `['projects']` also refreshes open doc reads.
  docs: (repo: string) => [...projectKeys.detail(repo), 'docs'] as const,
  doc: (repo: string, path: string, version?: number) =>
    [...projectKeys.docs(repo), path, version ?? 'latest'] as const,
};

/** Map the camelCase hook params onto RFC-0006's snake_case query params. */
function toApiParams(params: ProjectListParams): QueryParams {
  return {
    q: params.q,
    status: params.status,
    type: params.types,
    owner: params.owners,
    tag: params.tags,
    manifest_broken: params.manifestBroken,
    sync_failed: params.syncFailed,
    stale: params.stale,
    page: params.page,
    per_page: params.perPage,
  };
}

/** `GET /api/projects` — filtered, paginated repository list. */
export function useProjects(
  params: ProjectListParams = {},
): UseQueryResult<Paginated<ProjectListItem>, Error> {
  return useQuery({
    queryKey: projectKeys.list(params),
    queryFn: () => apiGet<Paginated<ProjectListItem>>('/api/projects', toApiParams(params)),
  });
}

/** `GET /api/projects/summary` — home/sync-issues rollup counts (no pagination). */
export function useProjectsHealthSummary(): UseQueryResult<ProjectSummary, Error> {
  return useQuery({
    queryKey: projectKeys.summary(),
    queryFn: () => apiGet<ProjectSummary>('/api/projects/summary'),
  });
}

/**
 * `GET /api/projects/:repo` — full project detail, embedding
 * nav/links/dependencies/replaces on top of the base shape (RFC-0006 §3). Powers
 * RFC-0010's project reader; disabled until `repo` is set.
 */
export function useProject(
  repo: string | undefined,
): UseQueryResult<ProjectDetailResponse, Error> {
  return useQuery({
    queryKey: projectKeys.detail(repo ?? ''),
    queryFn: () => apiGet<ProjectDetailResponse>(`/api/projects/${encodeURIComponent(repo!)}`),
    enabled: Boolean(repo),
  });
}
